import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { IconExternalLink } from '../../core';
import { textStyle } from '../../styles/mixins/_commonPageContentStyles';
import NewTabNote from '../NewTabNote';

interface Props {
  className?: string;
  title?: string;
  subtitle?: string;
  linkUrl?: string;
  icon?: ReactNode;
}

const DEFAULT_EXTERNAL_LINK_ICON: NonNullable<Props['icon']> = (
  <IconExternalLink className="icon-link" />
);

const ABSOLUTE_HTTP_URL = /^https?:\/\//i;

const ExternalLink = ({
  className = '',
  title = '',
  subtitle = '',
  linkUrl = '',
  icon = DEFAULT_EXTERNAL_LINK_ICON,
}: Props) => {
  const content = (
    <>
      <div>
        <span className="title">{title}</span>
        <span className="subtitle">{subtitle}</span>
      </div>
      <div className="icon-link">{icon}</div>
    </>
  );

  if (ABSOLUTE_HTTP_URL.test(linkUrl)) {
    return (
      <a href={linkUrl} className={className} target="_blank" rel="noopener noreferrer">
        {content}
        <NewTabNote />
      </a>
    );
  }

  return (
    <NavLink to={linkUrl} className={className}>
      {content}
    </NavLink>
  );
};

const ExternalLinkStyled = styled(ExternalLink)`
  border-radius: 0 0 0.5rem 0.5rem;
  background-color: ${({ theme }) => theme.neutralBackgroundMedium};
  border-top: 0.125rem solid ${({ theme }) => theme.neutralBorderMedium};
  padding: 1rem;
  text-decoration: none;
  color: ${({ theme }) => theme.neutralTextWeak};
  min-height: 10.25rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  transition:
    background-color 0.3s ease-in-out,
    border-color 0.3s ease-in-out,
    box-shadow 0.3s ease-in-out;

  /* Title and subtitle are spans, not a paragraph, so the page's own "p" rules cannot resize them. */
  .title {
    ${textStyle('h4Sb')}
    display: block;
    margin-bottom: 0.5rem;
    margin-top: 0;
    padding-right: 1.25rem;
    transition: color 0.3s ease-in-out;
    color: ${({ theme }) => theme.neutralTextWeak};
  }

  .subtitle {
    ${textStyle('h4')}
    display: block;
    padding-right: 1.25rem;
    color: ${({ theme }) => theme.neutralTextWeak};
  }

  .icon-link {
    display: flex;
    color: ${({ theme }) => theme.primaryText};
    align-self: flex-end;
  }

  @media (hover: hover) {
    &:hover {
      border-top: 0.125rem solid #2685a1; /* tokens-allow: pending design decision */
      background-color: ${({ theme }) => theme.neutralBackgroundBase};
      box-shadow: 0 0 0.5rem 0.125rem #141c2c0f; /* tokens-allow: pending design decision */

      .title {
        color: ${({ theme }) => theme.primaryTextHover};
      }
    }
  }
`;

export default ExternalLinkStyled;
